import { EyeIcon, EyeSlashIcon, PlusCircleIcon, SwitchIcon, TrashIcon } from '@synergy/theme';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage } from 'react-intl';
import { Grid, IconButton, Stack } from '@mui/material';
import { InformationOutlinedIcon } from '@synergy/theme-next';
import { PaloField, PaloFormGrid, PaloFormSection, PaloReadValue } from '@synergy/components-palo';
import { PoolButton, PoolModal, PoolSelect, PoolTextField, PoolTypography } from '@synergy/components';
import { XmlStoreFtpOption } from '@synergy/models';
import { connect } from 'react-redux';
import { fields } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { SupplierDetailOverviewFormValues } from '../models/supplier-detail-overview-form-values';

interface SupplierOverviewSectionFtpReduxProps {
    ftpServer: string;
    sftpServer: string;
}

interface SupplierOverviewSectionFtpOwnProps {
    supplier?: SupplierDetailOverviewFormValues;
    formikProps: FormikProps<SupplierDetailOverviewFormValues>;
    onDeleteFtpUser: () => void;
    onSaveNewFtpPassword: () => void;
    onAddFtp: () => void;
}

type SupplierOverviewSectionFtpProps = SupplierOverviewSectionFtpOwnProps & SupplierOverviewSectionFtpReduxProps;

interface SupplierOverviewSectionFtpState {
    deleteFtpModalVisible: boolean;
    changeFtpPasswordModalVisible: boolean;
    showActualFtpPassword: boolean;
    showNewFtpPassword: boolean;
    showNewModalFtpPassword: boolean;
    addFtpModalVisible: boolean;
    showAddFtpPassword: boolean;
}

class SupplierOverviewSectionFtpBase extends FormComponent<
    SupplierDetailOverviewFormValues,
    SupplierOverviewSectionFtpProps,
    SupplierOverviewSectionFtpState
> {
    public state: SupplierOverviewSectionFtpState = {
        deleteFtpModalVisible: false,
        changeFtpPasswordModalVisible: false,
        showActualFtpPassword: false,
        showNewFtpPassword: false,
        showNewModalFtpPassword: false,
        addFtpModalVisible: false,
        showAddFtpPassword: false,
    };

    public render() {
        const { ftpServer, sftpServer, supplier, formikProps } = this.props;

        const fieldNames = fields<SupplierDetailOverviewFormValues>();

        const handleDeleteModalClose = () => {
            this.setState({ deleteFtpModalVisible: false });
        };

        const handleDeleteFtpUser = () => {
            this.props.onDeleteFtpUser();
            handleDeleteModalClose();
        };

        const handleChangeModalClose = () => {
            this.setState({ changeFtpPasswordModalVisible: false });
            this.setState({ showNewModalFtpPassword: false });
            formikProps.resetForm();
        };

        const handleAddFtpModalClose = () => {
            this.setState({ addFtpModalVisible: false });
            this.setState({ showNewModalFtpPassword: false });
            formikProps.resetForm();
        };

        const handleChangeFtpPassword = () => {
            if (!formikProps.isValid) {
                return;
            }

            this.props.onSaveNewFtpPassword();
            handleChangeModalClose();
        };

        const handleAddFtp = () => {
            if (!formikProps.isValid) {
                return;
            }

            this.props.onAddFtp();
            handleAddFtpModalClose();
        };

        const deleteFtpActionChildren = [
            <PoolButton key="2" variant="text" styleType="firstLevel" onClick={handleDeleteModalClose}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" styleType="firstLevel" color="secondary" onClick={handleDeleteFtpUser}>
                <FormattedMessage id="app.common.delete" />
            </PoolButton>,
        ];

        const changeFtpPasswordActionChildren = [
            <PoolButton key="2" variant="text" styleType="firstLevel" onClick={handleChangeModalClose}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" styleType="firstLevel" onClick={handleChangeFtpPassword}>
                <FormattedMessage id="app.common.save" />
            </PoolButton>,
        ];

        const addFtpActionChildren = [
            <PoolButton key="2" variant="text" styleType="firstLevel" onClick={handleAddFtpModalClose}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" styleType="firstLevel" onClick={handleAddFtp}>
                <FormattedMessage id="app.common.save" />
            </PoolButton>,
        ];

        const xmlStoreFtpOption = [
            {
                value: XmlStoreFtpOption.None,
                displayValue: <FormattedMessage id="app.select.option.never" />,
            },
            {
                value: XmlStoreFtpOption.Shop,
                displayValue: <FormattedMessage id="app.select.option.shop" />,
            },
            {
                value: XmlStoreFtpOption.Manual,
                displayValue: <FormattedMessage id="app.select.option.manual" />,
            },
            {
                value: XmlStoreFtpOption.Both,
                displayValue: <FormattedMessage id="app.select.option.shopAndManual" />,
            },
        ];

        const actions = (
            <>
                {!formikProps.values.ftpUsername ? (
                    <PoolButton
                        variant="text"
                        styleType="header"
                        startIcon={<PlusCircleIcon />}
                        onClick={() => this.setState({ addFtpModalVisible: true })}
                        sx={{
                            color: theme => theme.palette.text.primary,
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        <FormattedMessage id="app.contactsAndOrganizations.detail.addFtp" />
                    </PoolButton>
                ) : (
                    <Stack flexDirection="row" gap={2}>
                        <PoolButton
                            variant="text"
                            styleType="header"
                            startIcon={<SwitchIcon />}
                            onClick={() => this.setState({ changeFtpPasswordModalVisible: true })}
                            sx={{
                                color: theme => theme.palette.text.primary,
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            <FormattedMessage id="app.contactsAndOrganizations.detail.changeFtpPassword" />
                        </PoolButton>
                        <PoolButton
                            variant="text"
                            styleType="header"
                            startIcon={<TrashIcon />}
                            onClick={() => this.setState({ deleteFtpModalVisible: true })}
                            sx={{
                                color: theme => theme.palette.text.primary,
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            <FormattedMessage id="app.contactsAndOrganizations.detail.deleteFtp" />
                        </PoolButton>
                    </Stack>
                )}
            </>
        );

        return (
            <PaloFormSection
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.sftp" />}
                action={actions}
            >
                {!formikProps.values.ftpUsername && (
                    <Stack gap={1} sx={{ mb: 2 }}>
                        <PoolTypography
                            typography="body1"
                            id="app.contactsAndOrganizations.detail.noAccountMessage"
                            component="span"
                        />
                    </Stack>
                )}
                {/* The access data reads as fields, the way every other read-only
                    value in the mask does — it used to be a grey box with its own
                    two-column list, bold labels and a 170-pixel first column. */}
                {formikProps.values.ftpUsername && (
                    <PaloFormGrid>
                        <PaloField label={<FormattedMessage id="app.contactsAndOrganizations.detail.sftp.link" />}>
                            <PaloReadValue mono={true}>{sftpServer}</PaloReadValue>
                        </PaloField>
                        <PaloField label={<FormattedMessage id="app.contactsAndOrganizations.detail.ftp.link" />}>
                            <PaloReadValue mono={true}>{ftpServer}</PaloReadValue>
                        </PaloField>
                        <PaloField label={<FormattedMessage id="app.contactsAndOrganizations.detail.sftp.username" />}>
                            <PaloReadValue mono={true}>{supplier?.ftpUsername}</PaloReadValue>
                        </PaloField>
                        <PaloField
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.sftp.actualPassword" />}
                        >
                            <Stack direction="row" gap={0.5} alignItems="center">
                                <PaloReadValue mono={true}>
                                    {this.state.showActualFtpPassword
                                        ? supplier?.ftpPassword
                                        : supplier?.ftpPassword?.replace(/./g, '•')}
                                </PaloReadValue>
                                <IconButton
                                    size="small"
                                    aria-label="Passwort anzeigen"
                                    onClick={() =>
                                        this.setState({
                                            showActualFtpPassword: !this.state.showActualFtpPassword,
                                        })
                                    }
                                >
                                    {this.state.showActualFtpPassword ? <EyeIcon /> : <EyeSlashIcon />}
                                </IconButton>
                            </Stack>
                        </PaloField>
                    </PaloFormGrid>
                )}
                <Grid container={true} spacing={2} direction="column">
                    <Grid container={true} item={true} xs={12} spacing={2}>
                        <Grid item={true} xs={8}>
                            <Field name={fieldNames.xmlStoreFtpOption}>
                                {({ field, form, meta }: FieldProps<string>) => (
                                    <>
                                        <PoolSelect
                                            name={field.name}
                                            fullWidth={true}
                                            label={
                                                <FormattedMessage
                                                    id={'app.contactsAndOrganizations.detail.sftp.xmlStoreFtpOption'}
                                                />
                                            }
                                            disallowDefaultValue={true}
                                            options={xmlStoreFtpOption}
                                            onValueChange={value => form.setFieldValue(field.name, value)}
                                            error={!!meta.touched && !!meta.error}
                                            value={field.value ?? XmlStoreFtpOption.None}
                                            disabled={!form.values.ftpUsername}
                                        />
                                        {meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                    </>
                                )}
                            </Field>
                        </Grid>
                    </Grid>
                </Grid>
                {!formikProps.values.ftpUsername && (
                    <Stack direction="row" gap={0.75} alignItems="center" sx={{ pt: 1.5 }}>
                        <InformationOutlinedIcon sx={{ fontSize: 16 }} />
                        <PoolTypography
                            typography="body2"
                            id="app.contactsAndOrganizations.detail.sftp.xmlStoreFtp"
                            component="span"
                        />
                    </Stack>
                )}

                <PoolModal
                    isVisible={this.state.deleteFtpModalVisible}
                    title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteFtp" />}
                    handleClose={handleDeleteModalClose}
                    actionChildren={deleteFtpActionChildren}
                    hasModalTypography={true}
                >
                    <PoolTypography
                        id="app.contactsAndOrganizations.detail.deleteFtpMessage"
                        values={{ value: supplier?.name }}
                    />
                </PoolModal>
                <PoolModal
                    isVisible={this.state.changeFtpPasswordModalVisible}
                    title={<FormattedMessage id="app.contactsAndOrganizations.detail.changeFtpPassword" />}
                    handleClose={handleChangeModalClose}
                    actionChildren={changeFtpPasswordActionChildren}
                >
                    <Field name={fieldNames.newFtpPassword}>
                        {({ field, form }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                type={this.state.showNewModalFtpPassword ? 'text' : 'password'}
                                fullWidth={true}
                                label={<FormattedMessage id="app.common.newPassword" />}
                                error={this.hasError(form, field.name)}
                                helperText={
                                    this.getError(form, field.name) ?? (
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.sftp.minPasswordLength" />
                                    )
                                }
                                endAdornment={
                                    <IconButton
                                        size="large"
                                        onClick={() =>
                                            this.setState({
                                                showNewModalFtpPassword: !this.state.showNewModalFtpPassword,
                                            })
                                        }
                                    >
                                        {this.state.showNewModalFtpPassword ? <EyeIcon /> : <EyeSlashIcon />}
                                    </IconButton>
                                }
                            />
                        )}
                    </Field>
                </PoolModal>
                <PoolModal
                    isVisible={this.state.addFtpModalVisible}
                    title={<FormattedMessage id="app.contactsAndOrganizations.detail.addFtp" />}
                    handleClose={handleAddFtpModalClose}
                    actionChildren={addFtpActionChildren}
                >
                    <Field name={fieldNames.ftpPassword}>
                        {({ field, form }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                type={this.state.showAddFtpPassword ? 'text' : 'password'}
                                fullWidth={true}
                                label={<FormattedMessage id="app.common.newPassword" />}
                                error={this.hasError(form, field.name)}
                                helperText={
                                    this.getError(form, field.name) ?? (
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.sftp.minPasswordLength" />
                                    )
                                }
                                endAdornment={
                                    <IconButton
                                        size="large"
                                        onClick={() =>
                                            this.setState({
                                                showAddFtpPassword: !this.state.showAddFtpPassword,
                                            })
                                        }
                                    >
                                        {this.state.showAddFtpPassword ? <EyeIcon /> : <EyeSlashIcon />}
                                    </IconButton>
                                }
                            />
                        )}
                    </Field>
                </PoolModal>
            </PaloFormSection>
        );
    }
}

const mapStateToProps = (state: AppState) => {
    return {
        ftpServer: state.settings.ftpServer,
        sftpServer: state.settings.sftpServer,
    };
};

export const SupplierOverviewDetailSectionFtp = connect(mapStateToProps, {})(SupplierOverviewSectionFtpBase);
